import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Button, Spinner } from '@repo/shared-client/components/ui'
import { createFileRoute } from '@tanstack/react-router'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { AlertTriangleIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import type {
  TriggerJobMutation,
  TriggerJobMutationVariables,
  TriggerServerErrorMutation,
  TriggerServerErrorMutationVariables,
} from 'types/graphql'

const TRIGGER_JOB_MUTATION: TypedDocumentNode<TriggerJobMutation, TriggerJobMutationVariables> = gql`
  mutation TriggerJobMutation($jobName: String!) {
    triggerJob(jobName: $jobName) {
      success
      message
    }
  }
`

const TRIGGER_SERVER_ERROR_MUTATION: TypedDocumentNode<
  TriggerServerErrorMutation,
  TriggerServerErrorMutationVariables
> = gql`
  mutation TriggerServerErrorMutation {
    triggerServerError
  }
`

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/fatalerrorpage')({
  component: FatalErrorPage,
  head: () =>
    seo({
      title: `Admin - Fatal Error | ${env.VITE_APP_NAME}`,
      description: 'Admin Fatal Error Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function FatalErrorPage() {
  const { t } = useTranslation()

  const [triggerJob, { loading: jobLoading }] = useMutation(TRIGGER_JOB_MUTATION, {
    onCompleted: (mutationData) => {
      if (mutationData.triggerJob.success) {
        toast.success(mutationData.triggerJob.message)
      } else {
        toast.error(mutationData.triggerJob.message)
      }
    },
    onError: (mutationError) => {
      toast.error(t('Failed to trigger job: {{message}}', { message: mutationError.message }))
    },
  })

  const [triggerServerError, { loading: serverErrorLoading }] = useMutation(TRIGGER_SERVER_ERROR_MUTATION, {
    onError: () => {
      toast.success(t('Server error was triggered as expected (GraphQL mutation threw)'))
    },
  })

  const triggerClientError = () => {
    // biome-ignore lint/suspicious/noExplicitAny: Intentionally undefined object
    const undefinedObject: any = undefined
    return undefinedObject.nonExistentMethod()
  }

  const triggerErrorJob = () => {
    triggerJob({ variables: { jobName: 'ErrorJob' } })
  }

  return (
    <div className="mx-auto w-full max-w-screen-xl py-6">
      <div className="flex flex-col gap-4">
        <h1 className="font-semibold text-2xl tracking-tight">{t('Fatal Error Test Page')}</h1>
        <p className="text-muted-foreground">
          {t('This page provides various ways to trigger errors for testing error handling and monitoring systems.')}
        </p>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 className="font-medium text-lg">{t('Client-Side Error')}</h2>
            <p className="text-muted-foreground text-sm">
              {t('Triggers a client-side JavaScript error by calling a method on an undefined object.')}
            </p>
            <Button className="w-fit" onClick={triggerClientError} type="button">
              <AlertTriangleIcon />
              Trigger Client Error
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="font-medium text-lg">{t('Server-Side Error (GraphQL)')}</h2>
            <p className="text-muted-foreground text-sm">
              {t(
                'Triggers a server-side error via admin GraphQL mutation. The mutation throws and the client receives a GraphQL error.'
              )}
            </p>
            <Button className="w-fit" disabled={serverErrorLoading} onClick={() => triggerServerError()} type="button">
              {serverErrorLoading ? <Spinner className="mr-2 size-4" /> : null}
              <AlertTriangleIcon />
              Trigger Server Error
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="font-medium text-lg">{t('Error Job')}</h2>
            <p className="text-muted-foreground text-sm">
              {t(
                'Triggers a background job that intentionally throws an exception. This tests error handling in the job queue system.'
              )}
            </p>
            <Button className="w-fit" disabled={jobLoading} onClick={triggerErrorJob} type="button" variant="secondary">
              {jobLoading ? <Spinner className="mr-2 size-4" /> : null}
              <AlertTriangleIcon />
              Trigger Error Job
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
